// src/pages/pppoe-billing-groups/PPPoEBillingGroupsPage.tsx

import {
  Banknote,
  Calendar,
  CheckCircle2,
  Circle,
  Copy,
  Edit3,
  Eye,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCcw,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  Wifi,
  X,
  ZapOff,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface CurrentUserRouter {
  id?: string | number;
  router_id?: string | number;
  name?: string;
  ip_address?: string | null;
  wg_private_key?: string | null;
}

interface CurrentUserMe {
  id: string | number;
  username: string;
  email: string;
  client_id: string | number;
  routers?: CurrentUserRouter[];
  role?: { id: string | number; name: string };
}

interface SecretLite {
  id: string | number;
  username: string;
  account_number: string;
  phone_number?: string | null;
  email?: string | null;
  status?: boolean | null;
  amount_payable?: number | null;
  router_id: string | number;
}

interface PPPoEBillingGroupMember {
  id: string | number;
  group_id: string | number;
  secret_id: string | number;
  is_primary: boolean;
  created_at?: string;
  secret?: SecretLite | null;
}

interface PPPoEBillingGroup {
  id: string | number;
  client_id: string | number;
  name: string;
  payment_code: string;
  amount_payable: number;
  description?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  members?: PPPoEBillingGroupMember[];
}

interface GroupForm {
  name: string;
  payment_code: string;
  amount_payable: string;
  description: string;
  is_active: boolean;
  secret_ids: string[];
}

const normalizeList = <T,>(res: any): T[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res?.secrets)) return res.secrets;
  return [];
};

const getTotal = (res: any, fallbackLength: number) => {
  if (typeof res?.total === "number") return res.total;
  return fallbackLength;
};

const getErrorMessage = (err: any, fallback: string) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (err?.message) return err.message;

  if (err?.detail) {
    if (typeof err.detail === "string") return err.detail;
    return JSON.stringify(err.detail);
  }

  if (err?.error) return err.error;

  return fallback;
};

const formatKES = (value: number | string | null | undefined) => {
  const amount = Number(value || 0);
  return `KES ${new Intl.NumberFormat("en-KE").format(amount)}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-KE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const isInternalEmail = (email?: string | null) => {
  if (!email) return true;
  return email.includes("@aamx.local");
};

const getRouterIdFromUser = (user: CurrentUserMe | null) => {
  const router = user?.routers?.[0];

  if (!router) return "";
  if (router.id) return String(router.id);
  if (router.router_id) return String(router.router_id);

  if (router.wg_private_key) {
    try {
      const parsed = JSON.parse(router.wg_private_key);
      if (parsed?.router_id) return String(parsed.router_id);
    } catch {
      return "";
    }
  }

  return "";
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textAreaCls =
  "min-h-[96px] w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-2.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: React.ReactNode;
}> = ({ label, required, children, hint }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>

    {children}

    {hint && (
      <div className="text-[11.5px] font-medium leading-snug text-slate-500">
        {hint}
      </div>
    )}
  </div>
);

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
      active
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-slate-300 bg-slate-200 text-slate-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-slate-500"
      }`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-3xl font-black tracking-tight text-slate-900">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const PPPoEBillingGroupsPage: React.FC = () => {
  const { showToast } = useToast();

  const [currentUser, setCurrentUser] = useState<CurrentUserMe | null>(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const currentClientId = currentUser?.client_id
    ? String(currentUser.client_id)
    : "";

  const currentRouterId = useMemo(
    () => getRouterIdFromUser(currentUser),
    [currentUser],
  );

  const [loading, setLoading] = useState(false);
  const [loadingSecrets, setLoadingSecrets] = useState(false);
  const [saving, setSaving] = useState(false);

  const [groups, setGroups] = useState<PPPoEBillingGroup[]>([]);
  const [secrets, setSecrets] = useState<SecretLite[]>([]);

  const [search, setSearch] = useState("");
  const [secretSearch, setSecretSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PPPoEBillingGroup | null>(
    null,
  );
  const [detailsGroup, setDetailsGroup] = useState<PPPoEBillingGroup | null>(
    null,
  );

  const [form, setForm] = useState<GroupForm>({
    name: "",
    payment_code: "",
    amount_payable: "",
    description: "",
    is_active: true,
    secret_ids: [],
  });

  const selectableSecrets = useMemo(() => {
    if (!currentRouterId) return secrets;

    return secrets.filter(
      (s) => String(s.router_id) === String(currentRouterId),
    );
  }, [secrets, currentRouterId]);

  const filteredSelectableSecrets = useMemo(() => {
    const term = secretSearch.trim().toLowerCase();

    if (!term) return selectableSecrets;

    return selectableSecrets.filter((s) => {
      return [s.account_number, s.username, s.phone_number, s.email].some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(term),
      );
    });
  }, [selectableSecrets, secretSearch]);

  const selectedSecretIds = useMemo(
    () => new Set(form.secret_ids.map(String)),
    [form.secret_ids],
  );

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const groupStats = useMemo(
    () => ({
      activeGroups: groups.filter((g) => g.is_active).length,
      totalMembers: groups.reduce(
        (sum, g) => sum + (g.members?.length || 0),
        0,
      ),
      totalAmount: groups.reduce(
        (sum, g) => sum + Number(g.amount_payable || 0),
        0,
      ),
    }),
    [groups],
  );

  const loadMe = async () => {
    setLoadingMe(true);

    try {
      const me = await fetchApp<CurrentUserMe>("/auth/me");

      setCurrentUser(me);
      localStorage.setItem("user", JSON.stringify(me));

      if (me?.role?.name) {
        localStorage.setItem("userRole", me.role.name);
      }
    } catch (err: any) {
      showToast(getErrorMessage(err, "Failed to load current user."), "error");
    } finally {
      setLoadingMe(false);
    }
  };

  const loadSecrets = async () => {
    setLoadingSecrets(true);

    try {
      const params = new URLSearchParams({ page: "1", page_size: "500" });

      if (currentRouterId) {
        params.set("router_id", currentRouterId);
      }

      const res = await fetchApp(`/secrets/?${params.toString()}`);

      setSecrets(normalizeList<SecretLite>(res));
    } catch (err: any) {
      showToast(
        getErrorMessage(err, "Failed to load PPPoE accounts."),
        "error",
      );
    } finally {
      setLoadingSecrets(false);
    }
  };

  const loadGroups = async (nextPage = page, nextSearch = search) => {
    if (!currentClientId) {
      setGroups([]);
      setTotal(0);
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        page_size: String(pageSize),
        client_id: currentClientId,
      });

      if (nextSearch.trim()) {
        params.set("search", nextSearch.trim());
      }

      const res = await fetchApp(`/pppoe-billing-groups/?${params.toString()}`);
      const items = normalizeList<PPPoEBillingGroup>(res);

      setGroups(items);
      setTotal(getTotal(res, items.length));
    } catch (err: any) {
      showToast(
        getErrorMessage(err, "Failed to load PPPoE billing groups."),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    loadSecrets();
    loadGroups(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentRouterId, currentClientId]);

  useEffect(() => {
    if (!currentUser) return;

    loadGroups(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const resetForm = () => {
    setForm({
      name: "",
      payment_code: "",
      amount_payable: "",
      description: "",
      is_active: true,
      secret_ids: [],
    });

    setSecretSearch("");
  };

  const openCreateModal = () => {
    setEditingGroup(null);
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (group: PPPoEBillingGroup) => {
    setEditingGroup(group);
    setSecretSearch("");

    setForm({
      name: group.name || "",
      payment_code: group.payment_code || "",
      amount_payable: String(group.amount_payable || ""),
      description: group.description || "",
      is_active: Boolean(group.is_active),
      secret_ids: (group.members || []).map((m) => String(m.secret_id)),
    });

    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingGroup(null);
    resetForm();
  };

  const handleFormChange = (
    field: keyof GroupForm,
    value: string | boolean | string[],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSecretSelection = (secretId: string) => {
    setForm((prev) => ({
      ...prev,
      secret_ids: prev.secret_ids.includes(secretId)
        ? prev.secret_ids.filter((id) => id !== secretId)
        : [...prev.secret_ids, secretId],
    }));
  };

  const validateForm = () => {
    const amount = Number(form.amount_payable);

    if (!currentClientId) {
      showToast("Client ID was not found.", "error");
      return false;
    }

    if (!form.name.trim()) {
      showToast("Group name is required.", "error");
      return false;
    }

    if (!form.amount_payable.trim() || Number.isNaN(amount)) {
      showToast("Amount payable is required.", "error");
      return false;
    }

    if (amount <= 0) {
      showToast("Amount must be greater than 0.", "error");
      return false;
    }

    if (!editingGroup && form.secret_ids.length === 0) {
      showToast("Select at least one PPPoE account.", "error");
      return false;
    }

    return true;
  };

  const syncEditedMembers = async (
    group: PPPoEBillingGroup,
    nextSecretIds: string[],
  ) => {
    const currentIds = new Set(
      (group.members || []).map((m) => String(m.secret_id)),
    );

    const nextIds = new Set(nextSecretIds.map(String));

    const toAdd = nextSecretIds.filter((id) => !currentIds.has(id));
    const toRemove = Array.from(currentIds).filter((id) => !nextIds.has(id));

    if (toAdd.length > 0) {
      await fetchApp(`/pppoe-billing-groups/${group.id}/members`, {
        method: "POST",
        body: JSON.stringify({ secret_ids: toAdd }),
      });
    }

    for (const secretId of toRemove) {
      await fetchApp(`/pppoe-billing-groups/${group.id}/members/${secretId}`, {
        method: "DELETE",
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = {
        client_id: currentClientId,
        name: form.name.trim(),
        payment_code: form.payment_code.trim() || null,
        amount_payable: Number(form.amount_payable),
        description: form.description.trim() || null,
        is_active: form.is_active,
        secret_ids: form.secret_ids,
      };

      if (editingGroup) {
        const { secret_ids, client_id, ...updatePayload } = payload;

        await fetchApp(`/pppoe-billing-groups/${editingGroup.id}`, {
          method: "PATCH",
          body: JSON.stringify(updatePayload),
        });

        await syncEditedMembers(editingGroup, form.secret_ids);

        showToast("Billing group updated.", "success");
      } else {
        await fetchApp("/pppoe-billing-groups/", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        showToast("Billing group created.", "success");
      }

      closeModal();
      setPage(1);
      await loadGroups(1, search);
    } catch (err: any) {
      showToast(getErrorMessage(err, "Failed to save billing group."), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (group: PPPoEBillingGroup) => {
    if (
      !window.confirm(
        `Delete "${group.name}"? This will remove the group and its members.`,
      )
    ) {
      return;
    }

    try {
      await fetchApp(`/pppoe-billing-groups/${group.id}`, { method: "DELETE" });

      showToast("Billing group deleted.", "success");
      await loadGroups(page, search);
    } catch (err: any) {
      showToast(
        getErrorMessage(err, "Failed to delete billing group."),
        "error",
      );
    }
  };

  const handleCopyPaymentCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      showToast("Payment code copied.", "success");
    } catch {
      showToast("Could not copy payment code.", "error");
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadGroups(1, search);
  };

  const handleRefresh = () => {
    setSearch("");
    setPage(1);
    loadMe();
  };

  if (loadingMe) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#eef3f8]">
        <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
        </div>

        <p className="text-[13px] font-bold text-slate-500">
          Loading billing groups…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Shared Payment Codes
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  PPPoE Billing Groups
                </h1>

                <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                  Manage shared payment codes that activate multiple PPPoE
                  accounts from one payment.
                </p>

                {/* {currentRouterId && (
                  <p className="mt-2 inline-flex rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11.5px] font-bold text-slate-500">
                    Router scope: {currentRouterId}
                  </p>
                )} */}
              </div>

              <button
                type="button"
                onClick={openCreateModal}
                className={`${btnBase} shrink-0 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                <Plus className="h-4 w-4" />
                New Billing Group
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard
            label="Total Groups"
            value={total}
            sub="All matching groups"
            icon={<Users className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Active Groups"
            value={groupStats.activeGroups}
            sub="Currently enabled"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Members on Page"
            value={groupStats.totalMembers}
            sub="Linked PPPoE accounts"
            icon={<Wifi className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Page Value"
            value={formatKES(groupStats.totalAmount)}
            sub="Total grouped amount"
            icon={<Banknote className="h-5 w-5" />}
            tone="amber"
          />
        </div>

        {/* Toolbar */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-4 shadow-lg shadow-slate-300/25">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                className={`${inputCls} pl-10`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by group name or payment code…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>

            <button
              type="button"
              onClick={handleSearch}
              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
            >
              <Search className="h-4 w-4" />
              Search
            </button>

            <button
              type="button"
              onClick={handleRefresh}
              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
            >
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="text-[13px] font-bold text-slate-500">
              Loading billing groups…
            </p>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-12 text-center shadow-xl shadow-slate-300/30">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
              <Users className="h-8 w-8 text-slate-500" />
            </div>

            <p className="text-[15px] font-black text-slate-800">
              No billing groups found
            </p>

            <p className="mt-1 max-w-sm text-[13px] font-medium text-slate-500">
              Create a group to let one payment code activate multiple PPPoE
              accounts.
            </p>

            <button
              type="button"
              onClick={openCreateModal}
              className={`${btnBase} mt-5 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
            >
              <Plus className="h-4 w-4" />
              Create First Group
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {groups.map((group) => {
              const members = group.members || [];

              return (
                <div
                  key={String(group.id)}
                  className="group overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-300/35"
                >
                  <div
                    className={`h-1.5 w-full ${
                      group.is_active
                        ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                        : "bg-gradient-to-r from-slate-400 to-slate-500"
                    }`}
                  />

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="truncate text-[16px] font-black text-slate-900">
                            {group.name}
                          </h2>

                          <StatusBadge active={group.is_active} />
                        </div>

                        <p className="mt-1 line-clamp-1 text-[12.5px] font-medium text-slate-500">
                          {group.description || "No description provided"}
                        </p>
                      </div>

                      <div className="flex shrink-0 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDetailsGroup(group)}
                          title="View details"
                          className={`${btnBase} border border-slate-300 bg-slate-50 px-3 py-2 text-slate-500 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span className="hidden text-[12px] sm:inline">
                            View
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => openEditModal(group)}
                          title="Edit group"
                          className={`${btnBase} border border-slate-300 bg-slate-50 px-3 py-2 text-slate-500 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteGroup(group)}
                          title="Delete group"
                          className={`${btnBase} border border-rose-200 bg-rose-50 px-3 py-2 text-rose-600 shadow-sm hover:bg-rose-100`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-2.5">
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          Code
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            handleCopyPaymentCode(group.payment_code)
                          }
                          className="mt-1 flex items-center gap-1.5 text-[13px] font-black text-emerald-800 hover:text-emerald-900"
                        >
                          <span className="truncate">{group.payment_code}</span>
                          <Copy className="h-3.5 w-3.5 shrink-0 opacity-70" />
                        </button>
                      </div>

                      <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
                          Amount
                        </p>

                        <p className="mt-1 truncate text-[13px] font-black text-sky-900">
                          {formatKES(group.amount_payable)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-300 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Members
                        </p>

                        <p className="mt-1 text-[13px] font-black text-slate-800">
                          {members.length} acct
                          {members.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>

                    {members.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {members.slice(0, 5).map((member) => (
                          <span
                            key={String(member.id)}
                            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-600"
                          >
                            <Wifi className="h-3 w-3 text-emerald-500" />
                            {member.secret?.account_number ||
                              member.secret?.username ||
                              member.secret_id}
                          </span>
                        ))}

                        {members.length > 5 && (
                          <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-[11px] font-bold text-slate-500">
                            +{members.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 px-5 py-4 shadow-lg shadow-slate-300/25 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12.5px] font-medium text-slate-500">
            Page <span className="font-black text-slate-800">{page}</span> of{" "}
            <span className="font-black text-slate-800">{totalPages}</span>
            <span className="mx-2 text-slate-300">·</span>
            <span className="font-black text-slate-700">{total}</span> results
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
            >
              ← Previous
            </button>

            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-950/30">
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            <div className="flex items-start justify-between border-b border-slate-200 bg-slate-100/80 px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-[17px] font-black text-slate-900">
                    {editingGroup ? "Edit Billing Group" : "New Billing Group"}
                  </h2>

                  <p className="mt-0.5 text-[12.5px] font-medium text-slate-500">
                    Configure the payment code, amount, and linked PPPoE
                    accounts.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="max-h-[calc(92vh-86px)] overflow-y-auto"
            >
              <div className="grid grid-cols-1 gap-0 lg:grid-cols-[1fr_1.3fr]">
                <div className="space-y-4 border-r border-slate-200 bg-slate-50 p-6">
                  <Field label="Group Name" required>
                    <input
                      className={inputCls}
                      value={form.name}
                      onChange={(e) => handleFormChange("name", e.target.value)}
                      placeholder="e.g. Family Package"
                    />
                  </Field>

                  <Field
                    label="Payment Code"
                    hint="Leave blank to auto-generate."
                  >
                    <input
                      className={inputCls}
                      value={form.payment_code}
                      onChange={(e) =>
                        handleFormChange("payment_code", e.target.value)
                      }
                      placeholder="e.g. FAMILY001"
                    />
                  </Field>

                  <Field label="Amount Payable (KES)" required>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className={inputCls}
                      value={form.amount_payable}
                      onChange={(e) =>
                        handleFormChange("amount_payable", e.target.value)
                      }
                      placeholder="e.g. 3000"
                    />
                  </Field>

                  <Field label="Description">
                    <textarea
                      className={textAreaCls}
                      value={form.description}
                      onChange={(e) =>
                        handleFormChange("description", e.target.value)
                      }
                      placeholder="Optional notes about this group…"
                    />
                  </Field>

                  <Field label="Status">
                    <div className="grid grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => handleFormChange("is_active", true)}
                        className={`rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                          form.is_active
                            ? "border-emerald-200 bg-emerald-100 text-emerald-800 shadow-sm shadow-emerald-200/50"
                            : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                        }`}
                      >
                        Active
                      </button>

                      <button
                        type="button"
                        onClick={() => handleFormChange("is_active", false)}
                        className={`rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                          !form.is_active
                            ? "border-slate-300 bg-slate-200 text-slate-700 shadow-sm"
                            : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                        }`}
                      >
                        Inactive
                      </button>
                    </div>
                  </Field>
                </div>

                <div className="flex flex-col bg-slate-100/70">
                  <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[13.5px] font-black text-slate-900">
                          PPPoE Accounts
                        </p>

                        <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                          {form.secret_ids.length} account
                          {form.secret_ids.length !== 1 ? "s" : ""} selected
                        </p>
                      </div>

                      <span
                        className={`rounded-full border px-3 py-1 text-[11px] font-black ${
                          form.secret_ids.length > 0
                            ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                            : "border-slate-300 bg-slate-200 text-slate-600"
                        }`}
                      >
                        {form.secret_ids.length} / {selectableSecrets.length}
                      </span>
                    </div>

                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                      <input
                        className={`${inputCls} bg-slate-50 pl-10`}
                        value={secretSearch}
                        onChange={(e) => setSecretSearch(e.target.value)}
                        placeholder="Search username, account, phone, email…"
                      />
                    </div>
                  </div>

                  {loadingSecrets ? (
                    <div className="flex min-h-[300px] flex-col items-center justify-center gap-3">
                      <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />

                      <p className="text-[12.5px] font-bold text-slate-500">
                        Loading accounts…
                      </p>
                    </div>
                  ) : filteredSelectableSecrets.length === 0 ? (
                    <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 px-4 text-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Wifi className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[13px] font-black text-slate-700">
                        No matching accounts
                      </p>

                      <p className="text-[11.5px] font-medium text-slate-500">
                        Try a different search term.
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-[460px] space-y-2 overflow-y-auto p-3">
                      {filteredSelectableSecrets.map((secret) => {
                        const id = String(secret.id);
                        const selected = selectedSecretIds.has(id);

                        return (
                          <button
                            type="button"
                            key={id}
                            onClick={() => toggleSecretSelection(id)}
                            className={`w-full rounded-2xl border px-4 py-3 text-left transition-all duration-150 ${
                              selected
                                ? "border-emerald-200 bg-emerald-50 shadow-sm shadow-emerald-200/40"
                                : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-[13px] font-black text-slate-800">
                                  {secret.account_number || secret.username}
                                </p>

                                <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                                  {secret.username}
                                  {secret.phone_number
                                    ? ` · ${secret.phone_number}`
                                    : ""}
                                </p>
                              </div>

                              <div className="flex shrink-0 items-center gap-2">
                                <span className="text-[11px] font-black text-slate-500">
                                  {formatKES(secret.amount_payable || 0)}
                                </span>

                                {selected ? (
                                  <CheckCircle2 className="h-[18px] w-[18px] text-emerald-500" />
                                ) : (
                                  <Circle className="h-[18px] w-[18px] text-slate-300" />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2.5 border-t border-slate-200 bg-slate-100/80 px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className={`${btnBase} border border-slate-300 bg-slate-50 px-5 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className={`${btnBase} bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {editingGroup ? "Update Group" : "Create Group"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-950/30">
            <div
              className={`h-1.5 w-full ${
                detailsGroup.is_active
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                  : "bg-gradient-to-r from-slate-400 to-slate-500"
              }`}
            />

            <div className="flex items-start justify-between border-b border-slate-200 bg-slate-100/80 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <Users className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-[17px] font-black text-slate-900">
                    {detailsGroup.name}
                  </h2>

                  <p className="mt-0.5 text-[12px] font-medium text-slate-500">
                    Billing group details
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setDetailsGroup(null)}
                className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[calc(92vh-86px)] space-y-5 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  {
                    label: "Payment Code",
                    value: (
                      <button
                        type="button"
                        onClick={() =>
                          handleCopyPaymentCode(detailsGroup.payment_code)
                        }
                        className="flex items-center gap-1.5 text-[14px] font-black text-emerald-700 hover:text-emerald-900"
                      >
                        {detailsGroup.payment_code}
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    ),
                    cls: "border-emerald-200 bg-emerald-50",
                  },
                  {
                    label: "Amount Payable",
                    value: (
                      <span className="text-[14px] font-black text-slate-800">
                        {formatKES(detailsGroup.amount_payable)}
                      </span>
                    ),
                    cls: "border-sky-200 bg-sky-50",
                  },
                  {
                    label: "Status",
                    value: <StatusBadge active={detailsGroup.is_active} />,
                    cls: "border-slate-300 bg-slate-100",
                  },
                  {
                    label: "Members",
                    value: (
                      <span className="text-[14px] font-black text-slate-800">
                        {detailsGroup.members?.length || 0}
                      </span>
                    ),
                    cls: "border-amber-200 bg-amber-50",
                  },
                ].map(({ label, value, cls }) => (
                  <div
                    key={label}
                    className={`rounded-2xl border px-4 py-3 ${cls}`}
                  >
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {label}
                    </p>

                    <div className="mt-1.5">{value}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Description
                </p>

                <p className="mt-1.5 text-[13px] font-medium text-slate-600">
                  {detailsGroup.description || "No description provided."}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  {
                    label: "Created At",
                    value: formatDateTime(detailsGroup.created_at),
                  },
                  {
                    label: "Updated At",
                    value: formatDateTime(detailsGroup.updated_at),
                  },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3"
                  >
                    <Calendar className="h-4 w-4 shrink-0 text-slate-400" />

                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {label}
                      </p>

                      <p className="mt-0.5 text-[13px] font-bold text-slate-700">
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="mb-3">
                  <p className="text-[13.5px] font-black text-slate-900">
                    Linked PPPoE Accounts
                  </p>

                  <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
                    Accounts activated when this group&apos;s payment code is
                    paid.
                  </p>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50">
                  <table className="min-w-full text-left">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-100 text-[10.5px] uppercase tracking-widest text-slate-500">
                        <th className="px-4 py-3 font-black">Account</th>
                        <th className="px-4 py-3 font-black">Contact</th>
                        <th className="px-4 py-3 font-black">Status</th>
                        <th className="px-4 py-3 text-right font-black">
                          Single Amount
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                      {(detailsGroup.members || []).length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center gap-2 text-slate-400">
                              <ZapOff className="h-6 w-6" />

                              <span className="text-[12.5px] font-bold">
                                No accounts linked.
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        (detailsGroup.members || []).map((member) => {
                          const s = member.secret;

                          return (
                            <tr
                              key={String(member.id)}
                              className="transition-colors hover:bg-emerald-50/60"
                            >
                              <td className="px-4 py-3">
                                <p className="text-[13px] font-black text-slate-800">
                                  {s?.account_number || member.secret_id}
                                </p>

                                <p className="text-[11.5px] font-medium text-slate-500">
                                  {s?.username || "—"}
                                </p>
                              </td>

                              <td className="px-4 py-3">
                                <div className="space-y-1 text-[12px] font-medium text-slate-500">
                                  <p className="flex items-center gap-1.5">
                                    <Phone className="h-3 w-3 text-slate-400" />
                                    {s?.phone_number || "—"}
                                  </p>

                                  <p className="flex items-center gap-1.5">
                                    <Mail className="h-3 w-3 text-slate-400" />
                                    {isInternalEmail(s?.email)
                                      ? "No email"
                                      : s?.email || "—"}
                                  </p>
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <StatusBadge active={Boolean(s?.status)} />
                              </td>

                              <td className="px-4 py-3 text-right text-[13px] font-black text-slate-700">
                                {formatKES(s?.amount_payable || 0)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setDetailsGroup(null);
                    openEditModal(detailsGroup);
                  }}
                  className={`${btnBase} border border-slate-300 bg-slate-50 px-5 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <Edit3 className="h-4 w-4" />
                  Edit Group
                </button>

                <button
                  type="button"
                  onClick={() => setDetailsGroup(null)}
                  className={`${btnBase} bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PPPoEBillingGroupsPage;
